export const curatedTypeDefs = `

scalar AWSDateTime

input AWSDateTimeFilterInput {
  _eq: AWSDateTime
  _exists: Boolean
  _gt: AWSDateTime
  _gte: AWSDateTime
  _in: [AWSDateTime!]
  _lt: AWSDateTime
  _lte: AWSDateTime
  _neq: AWSDateTime
  _nin: [AWSDateTime!]
}

scalar AWSJSON

type Aggregation {
  _avg: Float!
  _max: Float!
  _min: Float!
  _p1: Float!
  _p5: Float!
  _p25: Float!
  _p50: Float!
  _p75: Float!
  _p95: Float!
  _p99: Float!
  _stdDev: Float!
  _sum: Float!
}

enum AngleUnits {
  DEGREE_ANGLE
  RADIAN
}

input BooleanFilterInput {
  _eq: Boolean
  _exists: Boolean
  _neq: Boolean
}

type BusinessInsightsContact {
  """The name of the antenna involved in the contact."""
  antennaName: String

  """
  Unique identifier for the contact. Acts as a primary key for this structure.
  """
  contactId: String!

  """This represents the status of a contact."""
  contactStatus: ContactStatus!

  """This represents the AWS Customer ID which owns the contact."""
  customerId: String!

  """This field represents which region(s) that data was delivered to."""
  dataDeliveryRegions: [String!]

  """
  A list of abstractions for how data is transferred within the service on behalf of the customer.
  """
  dataflowList: [Dataflow!]

  """
  The end-time epoch of the contact. This does not include any post-pass time. This field acts as a secondary field for recording. That is, it is used when a time-partition of contacts is needed. (e.g. search, expiration, etc) 
  """
  endTime: AWSDateTime!

  """The airport code associated with the ground station region being used."""
  groundStationAirportCode: String

  """
  An abstraction for what location was primarily targeted for utilization
  """
  groundStationName: String

  """Maximum elevation (degrees)"""
  maximumElevation: Elevation

  """
  An abstraction for the expectations of the contact including dataflows and tracking.
  """
  missionProfileArn: String

  """Post-pass time of the contact epoch in seconds."""
  postPassEndTime: AWSDateTime

  """Pre-pass start time of the contact epoch in seconds."""
  prePassStartTime: AWSDateTime

  """
  An abstraction for what was utilized in this contact. e.g. antenna, recordings, sdrs. This allows us to extend what entities were involved in a contact.
  """
  reservationArns: [String!]

  """An abstraction for the vehicle involved in the contact."""
  satelliteArn: String
  schedulingRegion: String

  """
  The start time epoch of the contact. This does not include any pre-pass time.
  """
  startTime: AWSDateTime!
}

enum ConfigCapabilityType {
  ANTENNA_DOWNLINK
  ANTENNA_DOWNLINK_DEMOD_DECODE
  ANTENNA_UPLINK
  CONNECTION_PROXY
  DATAFLOW_ENDPOINT
  MANAGED_ANTENNA_DOWNLINK_DEMOD_DECODE
  S3_PLAYBACK
  S3_RECORDING
  TELEMETRY_SINK
  TRACKING
  UPLINK_ECHO
}

type ConfigIdToDagNodeMapEntry {
  key: String
  value: DagNode
}

enum ContactEphemerisUsageType {
  DAILY_EPHEMERIS_REFRESH_POINTING_ANGLE
  DAILY_EPHEMERIS_REFRESH_VALIDATE_VISIBILITY
  FINAL_POINTING_ANGLE
  FINAL_POINTING_ANGLE_REFERENCE
  SCHEDULING
  UPDATE_CONTACT
}

input ContactEphemerisUsageTypeFilterInput {
  _eq: ContactEphemerisUsageType
  _exists: Boolean
  _in: [ContactEphemerisUsageType!]
  _neq: ContactEphemerisUsageType
  _nin: [ContactEphemerisUsageType!]
}

type ContactGlobalDDBTableV2 {
  """The customer-side AWS Account ID"""
  awsAccountId: String!

  """Reason for cancelling."""
  cancellationReason: String

  """
  Cancellation timestamp of the record. Note originally in string format.
  """
  cancellationTimestamp: AWSDateTime
  capabilitiesToTest: [String!]
  configIdToDagNodeMap: [ConfigIdToDagNodeMapEntry!]

  """
  End time of the contact epoch in seconds. This does not include any postpass times.
  """
  contactEndTimeEpochSeconds: AWSDateTime!

  """The contact identifier."""
  contactId: String!

  """
  Start time of the contact epoch in seconds. This does not include any prepass times.
  """
  contactStartTimeEpochSeconds: AWSDateTime!

  """Contact status"""
  contactStatus: ContactStatus!

  """Contact type"""
  contactType: ContactType!

  """Signature Map for the Contact"""
  createReservationForRegionSignatureMaps: String

  """Create timestamp of the record. Note originally in string format."""
  createTimestamp: AWSDateTime!

  """AWS region of creation"""
  createdInRegion: String!

  """
  List storing EphemerisInfo objects storing info about the ephemeris used for scheduling, daily ephemeris refresh validate visibility or pointing angle, and final pointing angle calculations
  """
  ephemerisInfoList: [EphemerisInfo!]
  failedToScheduleReasons: [String!]

  """The ground station ID"""
  groundStationId: String!

  """Maximum elevation (degrees)"""
  maxElevationDegrees: Float!

  """Metering Archive ID"""
  meteringArchiveId: String

  """Metering Status"""
  meteringStatus: MeteringStatus

  """Metering Archive ID"""
  meteringUsage: MeteringUsage

  """Migration version."""
  migrationVersion: Int

  """Mission Profile AWS Resource Name"""
  missionProfileArn: String!
  obsoleteReservationArnList: [String!]

  """Payer Token"""
  payerToken: String

  """Post-pass time of the contact epoch in seconds."""
  postPassEndTimeEpochSeconds: AWSDateTime!

  """Pre-pass start time of the contact epoch in seconds."""
  prePassStartTimeEpochSeconds: AWSDateTime!

  """
  Rescheduling timestamp of the record. Note originally in string format.
  """
  reschedulingTimestamp: AWSDateTime
  reservationArnList: [String!]

  """Reserve Contact Step Function ID"""
  reserveContactStepFunctionId: String!

  """Satellite AWS Resource Name"""
  satelliteArn: String

  """Signature Map for the Contact"""
  signatureMap: String

  """AWS region of creation"""
  targetRegion: String!

  """
  List of RouteIds created in CapTel Routing Service for telemetry delivery
  """
  telemetryRouteList: [String!]

  """Update timestamp of the record. Note originally in string format."""
  updateTimestamp: AWSDateTime

  """Version number of the record."""
  versionNumber: Int!

  """Version timestamp of the record."""
  versionTimestamp: AWSDateTime
}

enum ContactStatus {
  AVAILABLE
  AWS_CANCELLED
  AWS_FAILED
  CANCELLED
  CANCELLING
  COMPLETED
  FAILED
  FAILED_TO_SCHEDULE
  PASS
  POSTPASS
  PREPASS
  RESCHEDULING
  SCHEDULED
  SCHEDULING
}

input ContactStatusFilterInput {
  _eq: ContactStatus
  _exists: Boolean
  _in: [ContactStatus!]
  _neq: ContactStatus
  _nin: [ContactStatus!]
}

type ContactTable {
  """Populated only when sk=CONSUMER-BUSINESS-INSIGHTS-CONTACT"""
  businessInsightsContact: BusinessInsightsContact

  """Populated only when sk=SOURCE-KINESIS-CP-CONTACTGLOBALDDBTABLEV2"""
  contactGlobalDDBTableV2Dto: ContactGlobalDDBTableV2

  """time to live attribute"""
  datalakeTtl: AWSDateTime!

  """Populated only when sk=SOURCE_KINESIS_CP_INREGIONCONTACTDDBTABLE"""
  inRegionContactDdbTable: InRegionContactDdbTable

  """pk = Primary Key = Contact ID"""
  pk: String!

  """sk = Sort Key = ContactTableSources Enum"""
  sk: String!

  """Version Attribute used for optimistic locking."""
  version: Int
}

enum ContactType {
  ANTENNA_SPECIFIC
  GROUND_STATION_SPECIFIC
}

type CpContactEvent010 {
  """
  The unique identifier associated with Contacts. This can be used to unambiguously fetch a Contact.
  """
  contactId: String!

  """
  The status of the contact. Provided here to support improved event filtering.
  """
  contactStatus: String!

  """
  The name part of the customer associated with the contact. Provided here to support improved event filtering.
  """
  customerNamePart: String!
}

enum CpCriticality {
  PREFERRED
  REMOVED
  REQUIRED
}

enum CpDataflowType {
  DDS
  DDS_V2
}

type CursorPart {
  key: String!
  value: String!
}

input CursorPartInput {
  key: String!
  value: String!
}

type DagNode {
  """ID of the config that this DAGNode is representing."""
  configId: String!

  """Version to uniquely identify nodes as well as paths from source to end"""
  dagNodeSemanticVersion: String

  """
  If this config is a dataflow endpoint, this is endpoint details. If this config is not a dataflow endpoint this will be null.
  """
  dataflowEndpointDetails: String

  """
  If this config is a dataflow endpoint, this is the config ID of the antenna resource for that dataflow. This is needed for generating the execution context and for executing dataflow endpoint reservations. If this config is not a dataflow endpoint this will be null.
  """
  dataflowSourceConfigId: String

  """Dataflow UUID"""
  dataflowUuid: String

  """Id of the execution context for fulfilling this config"""
  executionContextId: String

  """matchedCapabilityArns"""
  matchedCapabilityArns: [String!]

  """
  If this config is a dataflow endpoint, this is the config ID of the antenna resource for that dataflow. This is needed for generating the execution context and for executing dataflow endpoint reservations. If this config is not a dataflow endpoint this will be null.
  """
  nodeConfigCapabilityType: ConfigCapabilityType

  """
  A single config may be connected to multiple other configs if the config has multiple output types (for example, a demod decode mission profile will have output types in the dataflows). This maps an output type to the next config ID that it's connected to
  """
  outputTypeToNextConfigIdsMap: [StringToStringMapEntry!]

  """
  A single config may be connected to multiple other configs if the config has multiple output types (for example, a demod decode mission profile will have output types in the dataflows) This maps an output type to the previous config ID that it's connected to
  """
  outputTypeToPreviousConfigIdsMap: [StringToStringMapEntry!]

  """Region where this config needs to be reserved"""
  region: String

  """Id of the reservation for capability fulfilling this config"""
  reservationId: String

  """
  Status of the reservation for this Node. If no reservation has been created yet this will be null.
  """
  reservationStatus: ReservationStatus

  """Arn of the capability that is being reserved for this config"""
  reservedCapabilityArn: String

  """
  If this config is a S3 endpoint, this is endpoint details. If this config is not a S3 endpoint this will be null.
  """
  s3RecordingDetails: String

  """
  Set of config IDs that must share the same reservation group as this config
  """
  sameReservationGroupAsConfigIds: [String!]

  """
  VITA 49.2 Stream ID - Id embedded in the vita 49 packet.  Stream ids flow from source to end without changing.
  """
  vita49StreamId: Int
}

type Dataflow {
  dataflowId: String
  dataflowMetrics: DataflowMetrics
  destinationNode: DataflowNode
  rfCharacteristics: RfCharacteristics
  sourceNode: DataflowNode
  tags: [String!]
  vita49StreamCharacteristics: Vita49StreamCharacteristics
}

type DataflowMetrics {
  """
  The number of encapsulated bytes that the destination node of the dataflow received. Compare with bytes transferred to gauge customer experience.
  """
  bytesReceived: Long

  """
  The number of encapsulated bytes that the source node of the dataflow transferred.
  """
  bytesTransferred: Long
}

type DataflowNode {
  """This would be the capabilityArn"""
  nodeArn: String
  nodeRegion: String
  nodeType: ConfigCapabilityType
}

type DataflowTranslation {
  egress: TranslationDetail
  ingress: TranslationDetail
}

input DataflowTranslationFilterInput {
  _and: [DataflowTranslationFilterInput!]
  _exists: Boolean
  _not: DataflowTranslationFilterInput
  _or: [DataflowTranslationFilterInput!]
  egress: TranslationDetailFilterInput
  ingress: TranslationDetailFilterInput
}

enum DeliveryMechanism {
  DDX
  LAMINAR_FLOW
}

input DeliveryMechanismFilterInput {
  _eq: DeliveryMechanism
  _exists: Boolean
  _in: [DeliveryMechanism!]
  _neq: DeliveryMechanism
  _nin: [DeliveryMechanism!]
}

type DereferencedMissionProfileData {
  createdInRegion: String
  customerAwsAccountId: String
  minimumViableContactSeconds: Int
  missionProfileArn: String
  missionProfileId: String
  name: String
  postPassDurationSeconds: Int
  prePassDurationSeconds: Int
  telemetrySinkConfigArn: String
  trackingConfigArn: String
}

type Elevation {
  unit: AngleUnits!
  value: Float!
}

type EphemerisInfo {
  """Unique identifier for the ephemeris"""
  ephemerisId: String!

  """Type of ephemeris"""
  ephemerisSourceType: EphemerisSourceType

  """Timestamp when the ephemeris was updated"""
  updateTime: AWSDateTime!

  """Type of ephemeris usage"""
  usageType: ContactEphemerisUsageType!
}

input EphemerisInfoFilterInput {
  _and: [EphemerisInfoFilterInput!]
  _exists: Boolean
  _not: EphemerisInfoFilterInput
  _or: [EphemerisInfoFilterInput!]
  ephemerisId: StringFilterInput
  ephemerisSourceType: EphemerisSourceTypeFilterInput
  updateTime: TimestampFilterInput
  usageType: ContactEphemerisUsageTypeFilterInput
}

enum EphemerisSourceType {
  AZ_EL
  OEM
  SERVICE_MANAGED
  TLE
}

input EphemerisSourceTypeFilterInput {
  _eq: EphemerisSourceType
  _exists: Boolean
  _in: [EphemerisSourceType!]
  _neq: EphemerisSourceType
  _nin: [EphemerisSourceType!]
}

input FloatFilterInput {
  _eq: Float
  _exists: Boolean
  _gt: Float
  _gte: Float
  _in: [Float!]
  _lt: Float
  _lte: Float
  _neq: Float
  _nin: [Float!]
}

type Frequency {
  units: String
  value: Float
}

enum FrequencyBand {
  KA_BAND
  NONE
  S_BAND
  X_BAND
}

input GetGsocContactV2Input {
  contactId: String
}

type GsocAntennaDemodDecodeDetails {
  decodeConfigData: GsocDecodeConfigData!
  demodulationConfigData: GsocDemodulationConfigData!
  rfCharacteristics: GsocRfCharacteristics!
}

input GsocAntennaDemodDecodeDetailsFilterInput {
  _and: [GsocAntennaDemodDecodeDetailsFilterInput!]
  _exists: Boolean
  _not: GsocAntennaDemodDecodeDetailsFilterInput
  _or: [GsocAntennaDemodDecodeDetailsFilterInput!]
  decodeConfigData: GsocDecodeConfigDataFilterInput
  demodulationConfigData: GsocDemodulationConfigDataFilterInput
  rfCharacteristics: GsocRfCharacteristicsFilterInput
}

type GsocAntennaDownlinkDetails {
  rfCharacteristics: GsocRfCharacteristics!
}

input GsocAntennaDownlinkDetailsFilterInput {
  _and: [GsocAntennaDownlinkDetailsFilterInput!]
  _exists: Boolean
  _not: GsocAntennaDownlinkDetailsFilterInput
  _or: [GsocAntennaDownlinkDetailsFilterInput!]
  rfCharacteristics: GsocRfCharacteristicsFilterInput
}

type GsocAwsTemporaryCredentials {
  accessKeyId: String!
  expiration: AWSDateTime!
  secretAccessKey: String!
  sessionToken: String!
}

type GsocCapability {
  arn: String
  id: String!
  type: String!
}

type GsocCapabilityV2 {
  arn: String
  id: String!
  plots: [NamedImageV2!]!
  type: String!
}

input GsocCapabilityV2FilterInput {
  _and: [GsocCapabilityV2FilterInput!]
  _exists: Boolean
  _not: GsocCapabilityV2FilterInput
  _or: [GsocCapabilityV2FilterInput!]
  arn: StringFilterInput
  id: StringFilterInput
  plots: NamedImageV2FilterInput
  type: StringFilterInput
}

type GsocConfig {
  arn: String
  id: String!
  type: String!
}

input GsocConfigFilterInput {
  _and: [GsocConfigFilterInput!]
  _exists: Boolean
  _not: GsocConfigFilterInput
  _or: [GsocConfigFilterInput!]
  arn: StringFilterInput
  id: StringFilterInput
  type: StringFilterInput
}

type GsocConnectionProxyDetails {
  configArn: String
  dataflowTranslation: DataflowTranslation
}

input GsocConnectionProxyDetailsFilterInput {
  _and: [GsocConnectionProxyDetailsFilterInput!]
  _exists: Boolean
  _not: GsocConnectionProxyDetailsFilterInput
  _or: [GsocConnectionProxyDetailsFilterInput!]
  configArn: StringFilterInput
  dataflowTranslation: DataflowTranslationFilterInput
}

type GsocContactDetails {
  """Active contact version id for the contact"""
  activeContactVersionId: Int

  """Unique identifier for the contact"""
  contactId: String!

  """Current status of the contact."""
  contactStatus: ContactStatus!

  """Customer information for the contact."""
  customer: GsocCustomer!

  """List of dataflows and their detailed information"""
  dataflows: [GsocDataflow!]

  """time to live attribute"""
  datalakeTtl: AWSDateTime

  """List of regions where the contact data will land."""
  destinationRegions: [String!]!

  """Duration of the contact, in milliseconds"""
  durationMs: Float!

  """
  List storing EphemerisInfo objects storing info about the ephemeris used for scheduling, daily ephemeris refresh validate visibility or pointing angle, and reference/actual final pointing angle calculations
  """
  ephemerisInfoList: [EphemerisInfo!]

  """Failed to schedule reason"""
  failedToScheduleReasons: [String!]

  """Ground Stations."""
  groundStations: [GsocGroundStation!]

  """
  Configuration information from the Mission Profile at contact scheduling time.
  """
  missionProfile: GsocMissionProfile!

  """Unique identifier for the contact"""
  pk: String!

  """Satellite associated with the contact."""
  satellite: GsocSatellite

  """Region that the contact was scheduled from"""
  schedulingRegion: String!

  """
  Signature map containing troubleshooting information relevant to the contact.
  """
  signatureMap: [SignatureMapEntry!]

  """List of tags for tagging contacts for search purposes"""
  tags: [String!]

  """
  List of RouteIds created in CapTel Routing Service for telemetry delivery
  """
  telemetryRouteList: [String!]

  """The time range of the contact"""
  timeRange: TimeRange!
}

type GsocContactDynamoDbTable {
  """Active contact version id for the contact"""
  activeContactVersionId: Int

  """Unique identifier for the contact"""
  contactId: String!

  """Current status of the contact."""
  contactStatus: ContactStatus!

  """Customer information for the contact."""
  customer: GsocCustomer!

  """List of dataflows and their detailed information"""
  dataflows: [GsocDataflow!]

  """time to live attribute"""
  datalakeTtl: AWSDateTime

  """List of regions where the contact data will land."""
  destinationRegions: [String!]!

  """Duration of the contact, in milliseconds"""
  durationMs: Float!

  """
  List storing EphemerisInfo objects storing info about the ephemeris used for scheduling, daily ephemeris refresh validate visibility or pointing angle, and reference/actual final pointing angle calculations
  """
  ephemerisInfoList: [EphemerisInfo!]

  """Failed to schedule reason"""
  failedToScheduleReasons: [String!]

  """Ground Stations."""
  groundStations: [GsocGroundStation!]

  """
  Configuration information from the Mission Profile at contact scheduling time.
  """
  missionProfile: GsocMissionProfile!

  """Unique identifier for the contact"""
  pk: String!

  """Satellite associated with the contact."""
  satellite: GsocSatellite

  """Region that the contact was scheduled from"""
  schedulingRegion: String!

  """
  Signature map containing troubleshooting information relevant to the contact.
  """
  signatureMap: [SignatureMapEntry!]

  """List of tags for tagging contacts for search purposes"""
  tags: [String!]

  """
  List of RouteIds created in CapTel Routing Service for telemetry delivery
  """
  telemetryRouteList: [String!]

  """The time range of the contact"""
  timeRange: TimeRange!
}

type GsocContactEvent010 {
  """
  The unique identifier associated with Contacts. This can be used to unambiguously fetch a Contact.
  """
  contactId: String
}

type GsocContactIndexOpenSearch {
  """Active contact version id for the contact"""
  activeContactVersionId: Int

  """Unique identifier for the contact"""
  contactId: String!

  """Current status of the contact."""
  contactStatus: ContactStatus!

  """Customer information for the contact."""
  customer: GsocCustomer!

  """List of dataflows and their detailed information"""
  dataflows: [GsocDataflow!]

  """time to live attribute"""
  datalakeTtl: AWSDateTime

  """List of regions where the contact data will land."""
  destinationRegions: [String!]!

  """Duration of the contact, in milliseconds"""
  durationMs: Float!

  """
  List storing EphemerisInfo objects storing info about the ephemeris used for scheduling, daily ephemeris refresh validate visibility or pointing angle, and reference/actual final pointing angle calculations
  """
  ephemerisInfoList: [EphemerisInfo!]

  """Failed to schedule reason"""
  failedToScheduleReasons: [String!]

  """Ground Stations."""
  groundStations: [GsocGroundStation!]

  """
  Configuration information from the Mission Profile at contact scheduling time.
  """
  missionProfile: GsocMissionProfile!

  """Unique identifier for the contact"""
  pk: String!

  """Satellite associated with the contact."""
  satellite: GsocSatellite

  """Region that the contact was scheduled from"""
  schedulingRegion: String!

  """
  Signature map containing troubleshooting information relevant to the contact.
  """
  signatureMap: [SignatureMapEntry!]

  """List of tags for tagging contacts for search purposes"""
  tags: [String!]

  """
  List of RouteIds created in CapTel Routing Service for telemetry delivery
  """
  telemetryRouteList: [String!]

  """The time range of the contact"""
  timeRange: TimeRange!
}

input GsocContactStatusFilterInput {
  _eq: ContactStatus
  _exists: Boolean
  _in: [ContactStatus!]
  _neq: ContactStatus
  _nin: [ContactStatus!]
}

type GsocContactV2 {
  """Active contact version id for the contact"""
  activeContactVersionId: Int

  """ACU report"""
  acuPlots: S3Object

  """Unique identifier for the contact"""
  contactId: String!

  """Antenna-level postpass plots (ACU, VTR, etc.)"""
  contactPlots: [NamedImageV2!]

  """Current status of the contact."""
  contactStatus: ContactStatus!

  """Customer information for the contact."""
  customer: GsocCustomer!

  """List of dataflows and their detailed information"""
  dataflows: [GsocDataflowV2!]

  """time to live attribute"""
  datalakeTtl: AWSDateTime

  """List of regions where the contact data will land."""
  destinationRegions: [String!]!

  """Duration of the contact, in milliseconds"""
  durationMs: Float!

  """
  List storing EphemerisInfo objects storing info about the ephemeris used for scheduling, daily ephemeris refresh validate visibility or pointing angle, and reference/actual final pointing angle calculations
  """
  ephemerisInfoList: [EphemerisInfo!]

  """Failed to schedule reason"""
  failedToScheduleReasons: [String!]

  """Ground Stations."""
  groundStations: [GsocGroundStation!]

  """
  Configuration information from the Mission Profile at contact scheduling time.
  """
  missionProfile: GsocMissionProfile!

  """Unique identifier for the contact"""
  pk: String!

  """Satellite associated with the contact."""
  satellite: GsocSatellite

  """Region that the contact was scheduled from"""
  schedulingRegion: String!

  """
  Signature map containing troubleshooting information relevant to the contact.
  """
  signatureMap: [SignatureMapEntry!]

  """List of tags for tagging contacts for search purposes"""
  tags: [String!]

  """
  List of RouteIds created in CapTel Routing Service for telemetry delivery
  """
  telemetryRouteList: [String!]

  """The time range of the contact"""
  timeRange: TimeRange!
}

type GsocContactV2Aggregation {
  activeContactVersionId: Aggregation!
  durationMs: Aggregation!
  satellite: GsocSatelliteAggregation!
}

input GsocContactV2FilterInput {
  _and: [GsocContactV2FilterInput!]
  _exists: Boolean
  _not: GsocContactV2FilterInput
  _or: [GsocContactV2FilterInput!]
  activeContactVersionId: IntegerFilterInput
  acuPlots: S3ObjectFilterInput
  contactId: StringFilterInput
  contactPlots: NamedImageV2FilterInput
  contactStatus: ContactStatusFilterInput
  customer: GsocCustomerFilterInput
  dataflows: GsocDataflowV2FilterInput
  datalakeTtl: TimestampFilterInput
  destinationRegions: StringFilterInput
  durationMs: FloatFilterInput
  ephemerisInfoList: EphemerisInfoFilterInput
  failedToScheduleReasons: StringFilterInput
  groundStations: GsocGroundStationFilterInput
  missionProfile: GsocMissionProfileFilterInput
  pk: StringFilterInput
  satellite: GsocSatelliteFilterInput
  schedulingRegion: StringFilterInput
  signatureMap: SignatureMapFilterInput
  tags: StringFilterInput
  telemetryRouteList: StringFilterInput
  timeRange: TimeRangeFilterInput
}

input GsocContactV2SortInput {
  activeContactVersionId: SortOrder
  acuPlots: S3ObjectSortInput
  contactId: SortOrder
  contactStatus: SortOrder
  customer: GsocCustomerSortInput
  datalakeTtl: SortOrder
  durationMs: SortOrder
  missionProfile: GsocMissionProfileSortInput
  pk: SortOrder
  satellite: GsocSatelliteSortInput
  schedulingRegion: SortOrder
  timeRange: TimeRangeSortInput
}

type GsocCustomer {
  awsAccountId: String
  awsGsaasAwsAccountNamePart: String!
  external: Boolean!
}

input GsocCustomerFilterInput {
  _and: [GsocCustomerFilterInput!]
  _exists: Boolean
  _not: GsocCustomerFilterInput
  _or: [GsocCustomerFilterInput!]
  awsAccountId: GsocStringFilterInput
  awsGsaasAwsAccountNamePart: GsocStringFilterInput
  external: BooleanFilterInput
}

input GsocCustomerSortInput {
  awsAccountId: SortOrder
  awsGsaasAwsAccountNamePart: SortOrder
  external: SortOrder
}

type GsocDataflow {
  dataflowId: String!
  destination: GsocNode!
  source: GsocNode!
  tags: [String!]!
}

type GsocDataflowV2 {
  dataflowId: String!
  destination: GsocNodeV2!
  source: GsocNodeV2!
  tags: [String!]!
}

input GsocDataflowV2FilterInput {
  _and: [GsocDataflowV2FilterInput!]
  _exists: Boolean
  _not: GsocDataflowV2FilterInput
  _or: [GsocDataflowV2FilterInput!]
  dataflowId: StringFilterInput
  destination: GsocNodeV2FilterInput
  source: GsocNodeV2FilterInput
  tags: StringFilterInput
}

enum GsocDataplaneType {
  DATA_DEFENDER
  LAMINAR_FLOW
}

input GsocDataplaneTypeFilterInput {
  _eq: GsocDataplaneType
  _exists: Boolean
  _in: [GsocDataplaneType!]
  _neq: GsocDataplaneType
  _nin: [GsocDataplaneType!]
}

type GsocDecodeConfigData {
  unvalidatedJson: String!
}

input GsocDecodeConfigDataFilterInput {
  _and: [GsocDecodeConfigDataFilterInput!]
  _exists: Boolean
  _not: GsocDecodeConfigDataFilterInput
  _or: [GsocDecodeConfigDataFilterInput!]
  unvalidatedJson: StringFilterInput
}

type GsocDemodulationConfigData {
  unvalidatedJson: String!
}

input GsocDemodulationConfigDataFilterInput {
  _and: [GsocDemodulationConfigDataFilterInput!]
  _exists: Boolean
  _not: GsocDemodulationConfigDataFilterInput
  _or: [GsocDemodulationConfigDataFilterInput!]
  unvalidatedJson: StringFilterInput
}

input GsocGetContactDetailsInput {
  date: AWSDateTime
  id: String!
}

type GsocGroundStation {
  antennaName: String
  groundStationId: String!
  groundStationName: String!
  region: String!
}

input GsocGroundStationFilterInput {
  _and: [GsocGroundStationFilterInput!]
  _exists: Boolean
  _not: GsocGroundStationFilterInput
  _or: [GsocGroundStationFilterInput!]
  antennaName: GsocStringFilterInput
  groundStationId: GsocStringFilterInput
  groundStationName: GsocStringFilterInput
  region: GsocStringFilterInput
}

type GsocIamRole {
  awsTemporaryCredentials: GsocAwsTemporaryCredentials
  isAuthorized: Boolean!
  name: String!
  roleArn: String!
}

type GsocIntegerRange {
  maximum: Int!
  minimum: Int!
}

input GsocIntegerRangeFilterInput {
  _and: [GsocIntegerRangeFilterInput!]
  _exists: Boolean
  _not: GsocIntegerRangeFilterInput
  _or: [GsocIntegerRangeFilterInput!]
  maximum: IntegerFilterInput
  minimum: IntegerFilterInput
}

input GsocListContactsPaginatedInput {
  cursor: [String!]
  limit: Int = 100
  sort: [ListGsocContactsSortInput!]
  where: ListGsocContactsFilterInput
}

type GsocListContactsPaginatedOutput {
  contacts: [GsocContactDetails!]!
  pageInfo: GsocPageInfo!
}

type GsocListIamRolesOutput {
  roles: [GsocIamRole!]!
}

type GsocManagedAntennaDemodDecodeDetails {
  managedRadioConfigId: String!
  rfCharacteristics: GsocRfCharacteristics!
}

input GsocManagedAntennaDemodDecodeDetailsFilterInput {
  _and: [GsocManagedAntennaDemodDecodeDetailsFilterInput!]
  _exists: Boolean
  _not: GsocManagedAntennaDemodDecodeDetailsFilterInput
  _or: [GsocManagedAntennaDemodDecodeDetailsFilterInput!]
  managedRadioConfigId: StringFilterInput
  rfCharacteristics: GsocRfCharacteristicsFilterInput
}

type GsocMissionProfile {
  missionProfileArn: String
  missionProfileId: String!
  missionProfileName: String
}

input GsocMissionProfileFilterInput {
  _and: [GsocMissionProfileFilterInput!]
  _exists: Boolean
  _not: GsocMissionProfileFilterInput
  _or: [GsocMissionProfileFilterInput!]
  missionProfileArn: GsocStringFilterInput
  missionProfileId: GsocStringFilterInput
  missionProfileName: GsocStringFilterInput
}

input GsocMissionProfileSortInput {
  missionProfileArn: SortOrder
  missionProfileId: SortOrder
  missionProfileName: SortOrder
}

type GsocNode {
  capability: GsocCapability!
  config: GsocConfig!
  nodeDetails: GsocNodeDetails!
  region: String!
  telemetryInfo: GsocTelemetryInformation
}

type GsocNodeAgentDetails {
  version: String
}

input GsocNodeAgentDetailsFilterInput {
  _and: [GsocNodeAgentDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeAgentDetailsFilterInput
  _or: [GsocNodeAgentDetailsFilterInput!]
  version: StringFilterInput
}

type GsocNodeDataplaneDataDefenderDetails {
  dataDeliveryServiceDestinationArn: String
  dataDeliveryServiceSourceArn: String
  securityDetails: GsocSecurityDetails
  vita49StreamId: Int
}

input GsocNodeDataplaneDataDefenderDetailsFilterInput {
  _and: [GsocNodeDataplaneDataDefenderDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeDataplaneDataDefenderDetailsFilterInput
  _or: [GsocNodeDataplaneDataDefenderDetailsFilterInput!]
  dataDeliveryServiceDestinationArn: StringFilterInput
  dataDeliveryServiceSourceArn: StringFilterInput
  securityDetails: GsocSecurityDetailsFilterInput
  vita49StreamId: IntegerFilterInput
}

type GsocNodeDataplaneDetails {
  dataDefender: GsocNodeDataplaneDataDefenderDetails
  dataflowType: GsocDataplaneType
  laminarFlow: GsocNodeDataplaneLaminarFlowDetails
}

input GsocNodeDataplaneDetailsFilterInput {
  _and: [GsocNodeDataplaneDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeDataplaneDetailsFilterInput
  _or: [GsocNodeDataplaneDetailsFilterInput!]
  dataDefender: GsocNodeDataplaneDataDefenderDetailsFilterInput
  dataflowType: GsocDataplaneTypeFilterInput
  laminarFlow: GsocNodeDataplaneLaminarFlowDetailsFilterInput
}

type GsocNodeDataplaneLaminarFlowDetails {
  agent: GsocNodeAgentDetails
  egressAddress: GsocRangedConnectionDetails
  ingressAddress: GsocRangedConnectionDetails
  laminarflowConfig: String
  vita49StreamId: Int
}

input GsocNodeDataplaneLaminarFlowDetailsFilterInput {
  _and: [GsocNodeDataplaneLaminarFlowDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeDataplaneLaminarFlowDetailsFilterInput
  _or: [GsocNodeDataplaneLaminarFlowDetailsFilterInput!]
  agent: GsocNodeAgentDetailsFilterInput
  egressAddress: GsocRangedConnectionDetailsFilterInput
  ingressAddress: GsocRangedConnectionDetailsFilterInput
  laminarflowConfig: StringFilterInput
  vita49StreamId: IntegerFilterInput
}

type GsocNodeDetails {
  dataplane: GsocNodeDataplaneDetails
  orchestration: GsocNodeOrchestrationDetails
}

input GsocNodeDetailsFilterInput {
  _and: [GsocNodeDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeDetailsFilterInput
  _or: [GsocNodeDetailsFilterInput!]
  dataplane: GsocNodeDataplaneDetailsFilterInput
  orchestration: GsocNodeOrchestrationDetailsFilterInput
}

type GsocNodeOrchestrationDetails {
  antennaDemodDecodeDetails: GsocAntennaDemodDecodeDetails
  antennaDownlinkDetails: GsocAntennaDownlinkDetails
  connectionProxyDetails: GsocConnectionProxyDetails
  managedAntennaDemodDecodeDetails: GsocManagedAntennaDemodDecodeDetails
  s3PlaybackDetails: GsocS3PlaybackDetails
  s3RecordingDetails: GsocS3RecordingDetails
  uplinkDetails: GsocUplinkDetails
  uplinkEchoDetails: GsocUplinkEchoDetails
}

input GsocNodeOrchestrationDetailsFilterInput {
  _and: [GsocNodeOrchestrationDetailsFilterInput!]
  _exists: Boolean
  _not: GsocNodeOrchestrationDetailsFilterInput
  _or: [GsocNodeOrchestrationDetailsFilterInput!]
  antennaDemodDecodeDetails: GsocAntennaDemodDecodeDetailsFilterInput
  antennaDownlinkDetails: GsocAntennaDownlinkDetailsFilterInput
  connectionProxyDetails: GsocConnectionProxyDetailsFilterInput
  managedAntennaDemodDecodeDetails: GsocManagedAntennaDemodDecodeDetailsFilterInput
  s3PlaybackDetails: GsocS3PlaybackDetailsFilterInput
  s3RecordingDetails: GsocS3RecordingDetailsFilterInput
  uplinkDetails: GsocUplinkDetailsFilterInput
  uplinkEchoDetails: GsocUplinkEchoDetailsFilterInput
}

type GsocNodeV2 {
  capability: GsocCapabilityV2!
  config: GsocConfig!
  nodeDetails: GsocNodeDetails!
  region: String!
  telemetryInfo: GsocTelemetryInformation
}

input GsocNodeV2FilterInput {
  _and: [GsocNodeV2FilterInput!]
  _exists: Boolean
  _not: GsocNodeV2FilterInput
  _or: [GsocNodeV2FilterInput!]
  capability: GsocCapabilityV2FilterInput
  config: GsocConfigFilterInput
  nodeDetails: GsocNodeDetailsFilterInput
  region: StringFilterInput
  telemetryInfo: GsocTelemetryInformationFilterInput
}

type GsocPageInfo {
  hasNextPage: Boolean!
  lastCursor: [String!]
  total: GsocPageInfoTotal
}

type GsocPageInfoTotal {
  relation: GsocPageInfoTotalRelation
  value: Int
}

enum GsocPageInfoTotalRelation {
  eq
  gte
}

type GsocRangedConnectionDetails {
  name: String
  portRange: GsocIntegerRange!
}

input GsocRangedConnectionDetailsFilterInput {
  _and: [GsocRangedConnectionDetailsFilterInput!]
  _exists: Boolean
  _not: GsocRangedConnectionDetailsFilterInput
  _or: [GsocRangedConnectionDetailsFilterInput!]
  name: StringFilterInput
  portRange: GsocIntegerRangeFilterInput
}

type GsocRfCharacteristics {
  """Bandwidth is not present during uplink or uplink echo."""
  bandwidth: Measurement
  centerFrequency: Measurement!
  polarization: Polarization!
}

input GsocRfCharacteristicsFilterInput {
  _and: [GsocRfCharacteristicsFilterInput!]
  _exists: Boolean
  _not: GsocRfCharacteristicsFilterInput
  _or: [GsocRfCharacteristicsFilterInput!]
  bandwidth: MeasurementFilterInput
  centerFrequency: MeasurementFilterInput
  polarization: PolarizationFilterInput
}

type GsocS3PlaybackDetails {
  bucketArn: String
  bucketRegion: String!
  keys: [String!]
  roleArn: String
}

input GsocS3PlaybackDetailsFilterInput {
  _and: [GsocS3PlaybackDetailsFilterInput!]
  _exists: Boolean
  _not: GsocS3PlaybackDetailsFilterInput
  _or: [GsocS3PlaybackDetailsFilterInput!]
  bucketArn: StringFilterInput
  bucketRegion: StringFilterInput
  keys: StringFilterInput
  roleArn: StringFilterInput
}

type GsocS3RecordingDetails {
  bucketArn: String
  bucketRegion: String!
  keyTemplate: String
  roleArn: String
}

input GsocS3RecordingDetailsFilterInput {
  _and: [GsocS3RecordingDetailsFilterInput!]
  _exists: Boolean
  _not: GsocS3RecordingDetailsFilterInput
  _or: [GsocS3RecordingDetailsFilterInput!]
  bucketArn: StringFilterInput
  bucketRegion: StringFilterInput
  keyTemplate: StringFilterInput
  roleArn: StringFilterInput
}

type GsocSatellite {
  """Customer owning this satellite"""
  customer: GsocCustomer!

  """AWS Satellite Resource ID"""
  id: String

  """Satellite Arn"""
  satelliteArn: String

  """Satellite Catalog Number"""
  satelliteCatalogNumber: Int

  """Satellite Name"""
  satelliteName: String

  """List of tags for tagging satellites for search purposes"""
  tags: [String!]
}

type GsocSatelliteAggregation {
  satelliteCatalogNumber: Aggregation!
}

input GsocSatelliteFilterInput {
  _and: [GsocSatelliteFilterInput!]
  _exists: Boolean
  _not: GsocSatelliteFilterInput
  _or: [GsocSatelliteFilterInput!]
  customer: GsocCustomerFilterInput
  satelliteArn: GsocStringFilterInput
  satelliteCatalogNumber: IntegerFilterInput
  satelliteName: GsocStringFilterInput
  tags: GsocStringFilterInput
}

input GsocSatelliteSortInput {
  satelliteArn: SortOrder
  satelliteCatalogNumber: SortOrder
  satelliteName: SortOrder
}

type GsocSecurityDetails {
  roleArn: String
  securityGroupIds: [String!]!
  subnetIds: [String!]!
}

input GsocSecurityDetailsFilterInput {
  _and: [GsocSecurityDetailsFilterInput!]
  _exists: Boolean
  _not: GsocSecurityDetailsFilterInput
  _or: [GsocSecurityDetailsFilterInput!]
  roleArn: StringFilterInput
  securityGroupIds: StringFilterInput
  subnetIds: StringFilterInput
}

input GsocStringFilterInput {
  _eq: String
  _exists: Boolean
  _gt: String
  _gte: String
  _in: [String!]
  _lt: String
  _lte: String
  _neq: String
  _nin: [String!]
  _startsWith: String
}

type GsocTelemetryInformation {
  telemetryScopeId: String!
  telemetrySourceArn: String!
}

input GsocTelemetryInformationFilterInput {
  _and: [GsocTelemetryInformationFilterInput!]
  _exists: Boolean
  _not: GsocTelemetryInformationFilterInput
  _or: [GsocTelemetryInformationFilterInput!]
  telemetryScopeId: StringFilterInput
  telemetrySourceArn: StringFilterInput
}

input GsocTimeRangeFilterInput {
  _exists: Boolean
  endTime: AWSDateTimeFilterInput
  startTime: AWSDateTimeFilterInput
}

type GsocUplinkDetails {
  rfCharacteristics: GsocRfCharacteristics!
  targetEirp: Measurement!
  transmitDisabled: Boolean!
}

input GsocUplinkDetailsFilterInput {
  _and: [GsocUplinkDetailsFilterInput!]
  _exists: Boolean
  _not: GsocUplinkDetailsFilterInput
  _or: [GsocUplinkDetailsFilterInput!]
  rfCharacteristics: GsocRfCharacteristicsFilterInput
  targetEirp: MeasurementFilterInput
  transmitDisabled: BooleanFilterInput
}

type GsocUplinkEchoDetails {
  enabled: Boolean!
  rfCharacteristics: GsocRfCharacteristics!
}

input GsocUplinkEchoDetailsFilterInput {
  _and: [GsocUplinkEchoDetailsFilterInput!]
  _exists: Boolean
  _not: GsocUplinkEchoDetailsFilterInput
  _or: [GsocUplinkEchoDetailsFilterInput!]
  enabled: BooleanFilterInput
  rfCharacteristics: GsocRfCharacteristicsFilterInput
}

type InRegionAntennaDownlinkConfigData {
  bandwidth: Frequency
  centerFrequency: Frequency
  polarity: Polarization
}

type InRegionAntennaDownlinkDemodDecodeConfigData {
  bandwidthValue: Frequency
  centerFrequency: Frequency
  decodeConfigData: InRegionDecodeConfigData
  demodulationConfigData: InRegionDemodulationConfigData
  polarity: Polarization
}

type InRegionAntennaDownlinkDemodDecodeExecutionContextData {
  antennaDownlinkDemodDecodeConfigData: InRegionAntennaDownlinkDemodDecodeConfigData
  antennaDownlinkDemodDecodeConfigId: String
  configArn: String
  dataflowType: CpDataflowType
  executionContextDataflows: [InRegionExecutionContextDataflowData!]
  outputToDataDeliveryServiceSourceArnMap: [StringToStringMapEntry!]
  outputToDestinationMtuMap: [StringToIntegerMapEntry!]
}

type InRegionAntennaDownlinkExecutionContextData {
  antennaDownlinkConfigData: InRegionAntennaDownlinkConfigData
  antennaDownlinkConfigId: String
  configArn: String
  dataDeliveryServiceSourceArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
}

type InRegionAntennaUplinkConfigData {
  centerFrequency: Frequency
  polarity: Polarization
  targetEirpUnit: String
  targetEirpValue: Float
  transmitDisabled: Boolean
}

type InRegionAntennaUplinkExecutionContextData {
  antennaUplinkConfigData: InRegionAntennaUplinkConfigData
  antennaUplinkConfigId: String
  configArn: String
  dataDeliveryServiceSourceArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
}

type InRegionConnectionProxyExecutionContextData {
  configArn: String
  dataflowTranslation: DataflowTranslation
  executionContextDataflowData: InRegionExecutionContextDataflowData
}

enum InRegionContactDataType {
  CONTACT_REQUEST
  EXECUTION_CONTEXT
}

type InRegionContactDdbTable {
  """The contact identifier."""
  contactId: String!

  """Present when CONTACT_REQUEST data type."""
  contactRequestData: InRegionContactRequestData

  """The data identifier for this record."""
  dataUuid: String!

  """Present when EXECUTION_CONTEXT data type"""
  executionContext: String

  """Present when EXECUTION_CONTEXT data type, after parsing."""
  executionContextStruct: InRegionExecutionContextData

  """Enumeration to determine the record type."""
  inRegionContactDataType: InRegionContactDataType!

  """Version number of the record."""
  versionNumber: Int!
}

type InRegionContactRequestData {
  antennaSystemId: String
  awsAccountId: String
  capabilitiesToTest: [String!]
  contactArn: String
  contactId: String
  contactType: ContactType
  createdInRegion: String
  dereferenceMissionProfileData: DereferencedMissionProfileData
  groundstationId: String
  requestedNodes: [RequestedNodesMapEntry!]
  satelliteArn: String
  targetRegion: String
}

type InRegionDataflowEndpointExecutionContextData {
  configArn: String
  dataDeliveryServiceDestinationArn: String
  dataDeliveryServiceSourceArn: String
  dataflowEndpointGroupArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
}

type InRegionDecodeConfigData {
  unvalidatedJSON: String
}

type InRegionDemodulationConfigData {
  unvalidatedJSON: String
}

type InRegionExecutionContextData {
  antennaDownlinkDemodDecodeExecutionContextData: InRegionAntennaDownlinkDemodDecodeExecutionContextData
  antennaDownlinkExecutionContextData: InRegionAntennaDownlinkExecutionContextData
  antennaUplinkExecutionContextData: InRegionAntennaUplinkExecutionContextData
  capabilityArn: String
  configArn: String
  connectionProxyExecutionContextData: InRegionConnectionProxyExecutionContextData
  dataflowEndpointExecutionContextData: InRegionDataflowEndpointExecutionContextData
  dataflowType: CpDataflowType
  executionContextId: String
  executionContextType: ConfigCapabilityType
  laminarFlowConfigString: String
  managedAntennaDownlinkDemodDecodeExecutionContextData: InRegionManagedAntennaDownlinkDemodDecodeExecutionContextData
  s3PlaybackExecutionContextData: InRegionS3PlaybackExecutionContextData
  s3RecordingExecutionContextData: InRegionS3RecordingExecutionContextData
  telemetryScopeId: String
  telemetrySourceArn: String
  trackingExecutionContextData: InRegionTrackingExecutionContextData
  type: ConfigCapabilityType
  uplinkEchoExecutionContextData: InRegionUplinkEchoExecutionContextData
}

type InRegionExecutionContextDataflowData {
  dataflowId: String
  dataflowRate: Measurement
  destinationIngressAddress: InRegionRangedSocketAddressInfo
  egressAddress: InRegionRangedSocketAddressInfo
  ingressAddress: InRegionRangedSocketAddressInfo
  isMultiChannel: Boolean
  sourceAccount: String
  sourceArn: String
  sourceEgressAddress: InRegionRangedSocketAddressInfo
  vita49StreamId: Int
}

type InRegionManagedAntennaDownlinkDemodDecodeConfigData {
  managedRadioConfigId: String
  spectrumConfig: InRegionManagedAntennaDownlinkDemodDecodeSpectrumConfig
  supportedOutputTypes: [InRegionManagedAntennaDownlinkDemodDecodeSupportedOutputType!]
}

type InRegionManagedAntennaDownlinkDemodDecodeExecutionContextData {
  configArn: String
  dataflowType: CpDataflowType
  executionContextDataflows: [InRegionExecutionContextDataflowData!]
  managedAntennaDownlinkDemodDecodeConfigData: InRegionManagedAntennaDownlinkDemodDecodeConfigData
  managedAntennaDownlinkDemodDecodeConfigId: String
}

type InRegionManagedAntennaDownlinkDemodDecodeSpectrumConfig {
  bandwidth: Frequency
  centerFrequency: Frequency
  polarization: Polarization
}

type InRegionManagedAntennaDownlinkDemodDecodeSupportedOutputType {
  description: String
  name: String
}

type InRegionRangedSocketAddressInfo {
  maximumPort: Int
  minimumPort: Int
  name: String
}

type InRegionS3PlaybackConfigData {
  bucketArn: String
  bucketRegion: String
  playbackFrequency: Frequency
  roleArn: String
}

type InRegionS3PlaybackConfigDto {
  awsAccountId: String
  configArn: String
  configId: String
  s3PlaybackConfigData: InRegionS3PlaybackConfigData
  type: ConfigCapabilityType
}

type InRegionS3PlaybackExecutionContextData {
  configArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
  s3PlaybackConfigDto: InRegionS3PlaybackConfigDto
}

type InRegionS3RecordingConfigData {
  bucketArn: String
  bucketRegion: String
  prefix: String
  roleArn: String
}

type InRegionS3RecordingConfigDto {
  awsAccountId: String
  configArn: String
  configId: String
  createdInRegion: String
  name: String
  region: String
  s3RecordingConfigData: InRegionS3RecordingConfigData
  type: ConfigCapabilityType
}

type InRegionS3RecordingExecutionContextData {
  dataDeliveryServiceDestinationArn: String
  dataDeliveryServiceSourceArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
  s3RecordingConfigDto: InRegionS3RecordingConfigDto
}

type InRegionTrackingExecutionContextData {
  autotrackCriticality: CpCriticality
}

type InRegionUplinkEchoConfigData {
  antennaUplinkArn: String
  centerFrequency: Frequency
  enabled: Boolean
  polarity: Polarization
}

type InRegionUplinkEchoExecutionContextData {
  configArn: String
  dataDeliveryServiceSourceArn: String
  dataflowType: CpDataflowType
  executionContextDataflowData: InRegionExecutionContextDataflowData
  uplinkEchoConfigData: InRegionUplinkEchoConfigData
  uplinkEchoConfigId: String
}

input IntegerFilterInput {
  _eq: Int
  _exists: Boolean
  _gt: Int
  _gte: Int
  _in: [Int!]
  _lt: Int
  _lte: Int
  _neq: Int
  _nin: [Int!]
}

input ListGsocContactV2Input {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [GsocContactV2SortInput!]
  where: GsocContactV2FilterInput
}

type ListGsocContactV2Output {
  aggregations: GsocContactV2Aggregation!
  pageInfo: PageInfo!
  results: [GsocContactV2!]!
}

input ListGsocContactsFilterInput {
  _and: [ListGsocContactsFilterInput!]
  _not: ListGsocContactsFilterInput
  _or: [ListGsocContactsFilterInput!]
  contactId: GsocStringFilterInput
  contactStatus: GsocContactStatusFilterInput
  customer: GsocCustomerFilterInput
  destinationRegions: GsocStringFilterInput
  groundStations: GsocGroundStationFilterInput
  missionProfile: GsocMissionProfileFilterInput
  satellite: GsocSatelliteFilterInput
  schedulingRegion: GsocStringFilterInput
  tags: GsocStringFilterInput
  telemetryRouteList: GsocStringFilterInput
  timeRange: GsocTimeRangeFilterInput
}

input ListGsocContactsSortInput {
  contactId: SortOrder
  contactStatus: SortOrder
  customer: GsocCustomerSortInput
  missionProfile: GsocMissionProfileSortInput
  satellite: GsocSatelliteSortInput
  schedulingRegion: SortOrder
  timeRange: TimeRangeSortInput
}

scalar Long

type Measurement {
  unit: String!
  value: Float!
}

input MeasurementFilterInput {
  _and: [MeasurementFilterInput!]
  _exists: Boolean
  _not: MeasurementFilterInput
  _or: [MeasurementFilterInput!]
  unit: StringFilterInput
  value: FloatFilterInput
}

enum MeteringStatus {
  METERING_FAILED
  METERING_QUEUED
  METERING_REQUIRED_CANCELLED
  METERING_REQUIRED_COMPLETED
  METERING_REQUIRED_NEGATIVE
}

enum MeteringUsage {
  MIDBAND
  NARROWBAND
  WIDEBAND
}

type NamedImage {
  imageBase64: String!
  name: String!
}

type NamedImageV2 {
  """Interactive HTML version of this plot, if available"""
  htmlImage: S3Object
  image: S3Object!
  name: String!
}

input NamedImageV2FilterInput {
  _and: [NamedImageV2FilterInput!]
  _exists: Boolean
  _not: NamedImageV2FilterInput
  _or: [NamedImageV2FilterInput!]
  htmlImage: S3ObjectFilterInput
  image: S3ObjectFilterInput
  name: StringFilterInput
}

type PageInfo {
  hasNextPage: Boolean!
  lastCursor: [CursorPart!]
  total: Total!
}

enum Polarization {
  BOTH
  LEFT_HAND
  NONE
  RIGHT_HAND
}

input PolarizationFilterInput {
  _eq: Polarization
  _exists: Boolean
  _in: [Polarization!]
  _neq: Polarization
  _nin: [Polarization!]
}

type Query {
  getGsocContactV2(cazJustification: String, input: GetGsocContactV2Input!): GsocContactV2
  getUserPermissionClaims: [String!]!
  gsocGetContactDetails(cazJustification: String, input: GsocGetContactDetailsInput!): GsocContactDetails
  gsocListContactsPaginated(cazJustification: String, input: GsocListContactsPaginatedInput!): GsocListContactsPaginatedOutput
  gsocListIamRoles(cazJustification: String): GsocListIamRolesOutput
  listGsocContactV2(cazJustification: String, input: ListGsocContactV2Input!): ListGsocContactV2Output
}

enum Relation {
  eq
  gte
}

type RequestedDagNodeInfo {
  dataflowUuid: String
  region: String
  versionedConfigId: String
  vita49StreamId: String
}

type RequestedNodesMapEntry {
  key: String
  value: RequestedDagNodeInfo
}

enum ReservationStatus {
  AWS_FAILED
  CANCELLED
  COMPLETED
  DELETED
  FAILED
  PASS
  POSTPASS
  PREPASS
  SCHEDULED
  SCHEDULING
}

type RfCharacteristics {
  frequencyBand: FrequencyBand
  polarization: Polarization
}

type S3Object {
  presignedUrl: String!
  s3Uri: String!
}

input S3ObjectFilterInput {
  _and: [S3ObjectFilterInput!]
  _exists: Boolean
  _not: S3ObjectFilterInput
  _or: [S3ObjectFilterInput!]
  s3Uri: StringFilterInput
}

input S3ObjectSortInput {
  s3Uri: SortOrder
}

type SignatureMapEntry {
  key: String
  value: Boolean
}

input SignatureMapFilterInput {
  _and: [SignatureMapFilterInput!]
  _exists: Boolean
  _not: SignatureMapFilterInput
  _or: [SignatureMapFilterInput!]
  key: StringFilterInput
  value: BooleanFilterInput
}

enum SortOrder {
  asc
  desc
}

input StringFilterInput {
  _eq: String
  _exists: Boolean
  _gt: String
  _gte: String
  _in: [String!]
  _lt: String
  _lte: String
  _neq: String
  _nin: [String!]
  _startsWith: String
}

type StringToIntegerMapEntry {
  key: String
  value: Int
}

type StringToStringMapEntry {
  key: String
  value: String
}

type TimeRange {
  endTime: AWSDateTime!
  startTime: AWSDateTime!
}

input TimeRangeFilterInput {
  _and: [TimeRangeFilterInput!]
  _exists: Boolean
  _not: TimeRangeFilterInput
  _or: [TimeRangeFilterInput!]
  endTime: TimestampFilterInput
  startTime: TimestampFilterInput
}

input TimeRangeSortInput {
  endTime: SortOrder
  startTime: SortOrder
}

input TimestampFilterInput {
  _eq: AWSDateTime
  _exists: Boolean
  _gt: AWSDateTime
  _gte: AWSDateTime
  _in: [AWSDateTime!]
  _lt: AWSDateTime
  _lte: AWSDateTime
  _neq: AWSDateTime
  _nin: [AWSDateTime!]
}

type Total {
  relation: Relation
  value: Int
}

type TranslationConfiguration {
  laminarFlowConfig: String
  type: DeliveryMechanism
  vpcAttachmentDetails: VpcAttachmentDetails
}

input TranslationConfigurationFilterInput {
  _and: [TranslationConfigurationFilterInput!]
  _exists: Boolean
  _not: TranslationConfigurationFilterInput
  _or: [TranslationConfigurationFilterInput!]
  laminarFlowConfig: StringFilterInput
  type: DeliveryMechanismFilterInput
  vpcAttachmentDetails: VpcAttachmentDetailsFilterInput
}

type TranslationDetail {
  deliveryMechanism: DeliveryMechanism
  translationConfiguration: TranslationConfiguration
}

input TranslationDetailFilterInput {
  _and: [TranslationDetailFilterInput!]
  _exists: Boolean
  _not: TranslationDetailFilterInput
  _or: [TranslationDetailFilterInput!]
  deliveryMechanism: DeliveryMechanismFilterInput
  translationConfiguration: TranslationConfigurationFilterInput
}

type Vita49StreamCharacteristics {
  vitaInterfaceType: Vita492SchemaType
  vitaStreamId: Int
}

enum Vita492SchemaType {
  AWS_GS_TYPE_1
  AWS_GS_TYPE_2
}

type VpcAttachmentDetails {
  customerRoleArn: String
  dataflowEndpointGroupArn: String
  securityGroupIdList: [String!]
  subnetIdList: [String!]
}

input VpcAttachmentDetailsFilterInput {
  _and: [VpcAttachmentDetailsFilterInput!]
  _exists: Boolean
  _not: VpcAttachmentDetailsFilterInput
  _or: [VpcAttachmentDetailsFilterInput!]
  customerRoleArn: StringFilterInput
  dataflowEndpointGroupArn: StringFilterInput
  securityGroupIdList: StringFilterInput
  subnetIdList: StringFilterInput
}
`;
